import { Component, Element, Prop, Watch, h } from '@stencil/core';

import dynamicEntryActions from '../actions/actions.dynamic-entry';
import { state as dynamicEntryState, on as onDynamicEntryState } from '../store/store.dynamic-entry';
import { DynamicEntryInstallationMode } from '../utils/utils.dynamic-entry';
import { DynamicEntryWorkflow } from '../utils/utils.landing';
@Component({
  tag: 'ico-dynamic-entry-hydrator',
  shadow: true,
})
export class DynamicEntryHydrator {
  @Element() el: HTMLElement;

  @Prop() offerCode: string;
  @Prop() dynamicEntryWorkflow: DynamicEntryWorkflow = DynamicEntryWorkflow.Unassigned;
  @Prop() enableMakeModel = true;
  @Prop() dealerId: string = '';
  @Prop() installationMode: DynamicEntryInstallationMode = DynamicEntryInstallationMode.Redirect;
  @Prop() tabList: Array<string> = ['vin', 'plate', 'ymm'];
  @Prop() defaultSelectedTab: string = 'vin';
  @Prop() enableTermsConditions: boolean = false;
  @Prop() vehicle: string;
  @Prop() inputVin: string = '';
  @Prop() mileage?: number;
  @Prop() dealerGroupId: string = '';
  @Prop() cobrandingGroupId: string = '';
  @Prop() ymmDisabled: boolean = false;
  @Prop() modalInfo: string = '';

  async componentWillLoad() {
    dynamicEntryActions.onDynamicEntryInit(
      this.offerCode,
      this.dynamicEntryWorkflow,
      this.dealerId,
      this.enableMakeModel,
      this.installationMode,
      this.tabList,
      this.defaultSelectedTab,
      this.vehicle,
      this.inputVin,
      this.mileage,
      this.dealerGroupId,
      this.cobrandingGroupId,
      this.modalInfo
    );
    this.setupStateProps();
    // Only call onAutoCompleteYmm if 'ymm' tab is present and inputVin is NOT present in dynamicEntryState
    if (this.tabList.includes('ymm') && !dynamicEntryState.inputVin && dynamicEntryState.inputVin !== '') {
      await dynamicEntryActions.onAutoCompleteYmm();
    }

    onDynamicEntryState('set', (prop, value) => {
      this.updateStateProps(prop, value);
    });

    onDynamicEntryState('reset', () => {
      this.setupStateProps();
    });
  }

  @Watch('modalInfo')
  watchModalInfoHandler(newValue: string) {
    dynamicEntryActions.onModalChange(newValue);
  }

  // updates only the changed prop
  updateStateProps(prop, value) {
    this.el.childNodes.forEach((element) => {
      element[prop] = value;
    });
  }

  // explicitly declare initial state props
  setupStateProps() {
    this.el.childNodes.forEach((element) => {
      element['isComponentRendering'] = dynamicEntryState.isComponentRendering;
      element['dynamicEntryWorkflow'] = dynamicEntryState.dynamicEntryWorkflow;
      element['enableMakeModel'] = dynamicEntryState.enableMakeModel;
      element['dealerId'] = this.dealerId;
      element['installationMode'] = this.installationMode;
      element['tabList'] = dynamicEntryState.tabList;
      element['tabHeaders'] = dynamicEntryState.tabHeaders;
      element['defaultSelectedTab'] = this.defaultSelectedTab;
      element['currentSelectedTab'] = dynamicEntryState.currentSelectedTab;
      element['isFetching'] = dynamicEntryState.isFetching;
      element['activeStep'] = dynamicEntryState.activeStep;
      element['vehiclePossibilities'] = dynamicEntryState.vehiclePossibilities;
      element['ymmSuggestions'] = dynamicEntryState.ymmSuggestions;
      element['year'] = dynamicEntryState.year;
      element['yearId'] = dynamicEntryState.yearId;
      element['make'] = dynamicEntryState.make;
      element['makeId'] = dynamicEntryState.makeId;
      element['model'] = dynamicEntryState.model;
      element['modelId'] = dynamicEntryState.modelId;
      element['trim'] = dynamicEntryState.trim;
      element['trimId'] = dynamicEntryState.trimId;
      element['color'] = dynamicEntryState.color;
      element['colorId'] = dynamicEntryState.colorId;
      element['transmission'] = dynamicEntryState.transmission;
      element['transmissionId'] = dynamicEntryState.transmissionId;
      element['engine'] = dynamicEntryState.engine;
      element['engineId'] = dynamicEntryState.engineId;
      element['drivetrain'] = dynamicEntryState.drivetrain;
      element['drivetrainId'] = dynamicEntryState.drivetrainId;
      element['vin'] = dynamicEntryState.vin;
      element['mileage'] = dynamicEntryState.mileage;
      element['inputVin'] = dynamicEntryState.inputVin;
      element['licensePlateNumber'] = dynamicEntryState.licensePlateNumber;
      element['licensePlateState'] = dynamicEntryState.licensePlateState;
      element['inputYmm'] = dynamicEntryState.inputYmm;
      element['vehicleId'] = dynamicEntryState.vehicleId;
      element['states'] = dynamicEntryState.states;
      element['shouldOpenFunnelModal'] = dynamicEntryState.shouldOpenFunnelModal;
      element['vinError'] = dynamicEntryState.vinError;
      element['lpError'] = dynamicEntryState.lpError;
      element['styleError'] = dynamicEntryState.styleError;
      element['mileageError'] = dynamicEntryState.mileageError;
      element['hasVinError'] = dynamicEntryState.hasVinError;
      element['hasLPError'] = dynamicEntryState.hasLPError;
      element['hasYmmError'] = dynamicEntryState.hasYmmError;
      element['hasYmmOption2'] = dynamicEntryState.hasYmmOption2;
      element['hasStyleError'] = dynamicEntryState.hasStyleError;
      element['hasMileageError'] = dynamicEntryState.hasMileageError;
      element['enableTermsConditions'] = this.enableTermsConditions;
      element['isTabRendering'] = dynamicEntryState.isTabRendering;
      element['furyUrl'] = dynamicEntryState.furyUrl;
      element['shouldShowIneligibleButton'] = dynamicEntryState.shouldShowIneligibleButton;
      element['vinActiveDealerUrl'] = dynamicEntryState.vinActiveDealerUrl;
      element['hasVinActiveAutotraderListingError'] = dynamicEntryState.hasVinActiveAutotraderListingError;
      element['sdContainer'] = dynamicEntryState.sdContainer;
      element['sdOemDealerCode'] = dynamicEntryState.oemDealerCode;
      element['ymmDisabled'] = this.ymmDisabled;
      element['vinModal'] = dynamicEntryState.vinModal;
      element['duplicateOfferModal'] = dynamicEntryState.duplicateOfferModal;
      element['hasMultipleTrims'] = dynamicEntryState.hasMultipleTrims;
      element['shouldShowStyleSelection'] = dynamicEntryState.shouldShowStyleSelection;
    });
  }

  render() {
    return (
      <div class="hydration-state hydration-state--dynamic-entry">
        <slot />
      </div>
    );
  }
}
